/**
 * Error Handling Actions for XState Services
 *
 * Provides standardized error handling actions that:
 * 1. Normalize errors to ServiceError format
 * 2. Send error alerts to the UI via alertView
 * 3. Update service context with error state
 *
 * @example
 * ```ts
 * import { handleServiceError, assignError } from '~/features/errors/actions';
 *
 * setup({
 *   types: {
 *     context: {} as { error: ServiceError | null },
 *   },
 *   actions: {
 *     handleError: handleServiceError,
 *     assignError: assignError,
 *   },
 * })
 *
 * // In your machine:
 * onError: {
 *   actions: ['handleError'],
 * }
 * ```
 */

import { assign } from 'xstate';
import { sendErrorAlertAction } from '~/features/alerts/service-actions';
import type { ServiceError } from './types';
import { normalizeError } from './types';

/**
 * Scope for error handling actions
 */
interface ErrorActionScope {
  system?: {
    get: (id: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState system.send accepts any event
      send: (event: any) => void;
    };
  };
  event: {
    error?: unknown;
  };
}

/**
 * Handle service errors with normalized format and UI alert
 *
 * This action:
 * 1. Normalizes the error to ServiceError format
 * 2. Sends an error alert to the UI
 * 3. Logs the error for debugging
 *
 * Usage: In machine setup -> actions: { handleError: handleServiceError }
 * Usage: In machine states -> onError: { actions: 'handleError' }
 */
export const handleServiceError = ({ system, event }: ErrorActionScope) => {
  const error = event.error;
  const serviceError = normalizeError(error);

  // Send error alert to UI
  if (system) {
    try {
      sendErrorAlertAction({
        system,
        event: {
          payload: {
            text: serviceError.code,
            error: serviceError,
          },
        },
      } as any); // eslint-disable-line @typescript-eslint/no-explicit-any -- sendErrorAlertAction expects specific event shape
    } catch (err) {
      console.warn('[ServiceError] Could not send error alert:', err);
    }
  }
};

/**
 * Assign normalized error to context
 *
 * Usage: In machine setup -> actions: { assignError: assignError }
 * Usage: In machine states -> onError: { actions: 'assignError' }
 */
export const assignError = assign({
  error: ({ event }: ErrorActionScope): ServiceError => normalizeError(event.error),
});

/**
 * Combined error handling action that assigns and alerts
 *
 * This is a convenience action that combines handleServiceError and assignError
 * Use this when you want both UI alert and context update
 *
 * Usage: In machine setup -> actions: { handleAndAssignError: handleAndAssignError }
 * Usage: In machine states -> onError: { actions: 'handleAndAssignError' }
 */
export const handleAndAssignError = [handleServiceError, assignError];

/**
 * Clear error from context
 *
 * Usage: In machine setup -> actions: { clearError: clearError }
 * Usage: In machine states -> entry: 'clearError'
 */
export const clearError = assign({
  error: () => null,
});

/**
 * Create a custom error handler with specific error code mapping
 *
 * @example
 * ```ts
 * const handleApiError = createErrorHandler({
 *   401: { code: 'UNAUTHORIZED', message: 'Please log in again' },
 *   403: { code: 'FORBIDDEN', message: 'You do not have permission' },
 *   404: { code: 'NOT_FOUND', message: 'Resource not found' },
 *   500: { code: 'SERVER_ERROR', message: 'Server error occurred' },
 * });
 *
 * setup({
 *   actions: {
 *     handleApiError,
 *   },
 * })
 * ```
 */
export const createErrorHandler =
  (statusCodeMap: Record<number, { code: string; message: string }>) =>
  ({ system, event }: ErrorActionScope) => {
    const error = event.error;
    let serviceError = normalizeError(error);

    // Map HTTP status codes to custom error messages
    if (serviceError.statusCode && serviceError.statusCode in statusCodeMap) {
      const mapping = statusCodeMap[serviceError.statusCode];
      serviceError = {
        ...serviceError,
        code: mapping.code,
        message: mapping.message,
      };
    }

    console.error('[ServiceError]', serviceError.code, serviceError.message, serviceError);

    // Send error alert to UI
    if (system) {
      try {
        sendErrorAlertAction({
          system,
          event: {
            payload: {
              text: serviceError.code,
              error: serviceError,
            },
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- sendErrorAlertAction expects specific event shape
        } as any);
      } catch (err) {
        console.warn('[ServiceError] Could not send error alert:', err);
      }
    }
  };

/**
 * Create an assign action that maps status codes to custom errors
 *
 * @example
 * ```ts
 * const assignApiError = createErrorAssigner({
 *   401: { code: 'UNAUTHORIZED', message: 'Please log in again' },
 *   403: { code: 'FORBIDDEN', message: 'You do not have permission' },
 * });
 *
 * setup({
 *   actions: {
 *     assignApiError,
 *   },
 * })
 * ```
 */
export const createErrorAssigner = (
  statusCodeMap: Record<number, { code: string; message: string }>
) =>
  assign(({ event }: ErrorActionScope) => {
    const error = event.error;
    let serviceError = normalizeError(error);

    // Map HTTP status codes to custom error messages
    if (serviceError.statusCode && serviceError.statusCode in statusCodeMap) {
      const mapping = statusCodeMap[serviceError.statusCode];
      serviceError = {
        ...serviceError,
        code: mapping.code,
        message: mapping.message,
      };
    }

    return { error: serviceError };
  });
